/**
 * Card de Vistoria no Kanban somente leitura.
 *
 * @version 1.8.0
 * RELEASE: 2026-09-26 v1.8.0 - Exibe o Tipo de Checklist junto ao nome do
 *          template, permitindo identificar rapidamente a finalidade da vistoria.
 * RELEASE: 2026-09-26 v1.7.0 - Placa volta ao formato Mercosul compacto
 *          (tamanho anterior à padronização com as listagens de veículos),
 *          mantendo a interação de abrir o veículo quando permitido.
 * RELEASE: 2026-09-26 v1.5.0 - Reutiliza a interação centralizada da placa
 *          para abrir o veículo, preservando clique e navegação por teclado.
 * RELEASE: 2026-09-26 v1.4.0 - Exibe o modo configurado do responsável sem
 *          confundir a origem da atribuição com o usuário executor.
 * RELEASE: 2026-09-26 v1.3.0 - Placa com affordance de modal do veículo (mesmo
 *          padrão do proprietário) e descrição do veículo em até 2 linhas.
 * RELEASE: 2026-09-26 v1.2.0 - Progresso movido para a linha da placa, alerta
 *          antifraude sem badge e proprietário com affordance de modal separado.
 * RELEASE: 2026-09-26 v1.1.0 - Progresso no padrão de Contratos, placa Mercosul,
 *          proprietário histórico clicável e responsável com avatar/nome.
 * RELEASE: 2026-09-26 v1.0.0 - Paridade visual com o card de Propostas.
 */
import { AlertTriangle, CalendarClock, CalendarPlus, Car, ClipboardCheck, ExternalLink, Hand, Repeat } from 'lucide-react';

/** Rótulos da frequência da periodicidade (v1.4.0). */
const FREQUENCY_LABELS: Record<string, string> = { daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal' };
const ASSIGNEE_MODE_LABELS: Record<string, string> = { user: 'Usuário', queue: 'Fila global', owner: 'Proprietário', provider: 'Prestador' };
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { SmartAvatar } from '@/components/ui/smart-avatar';
// v1.7.0: placa volta a usar o componente Mercosul compacto do padrão anterior.
import { VehiclePlateChip } from '@/components/vehicles/VehiclePlateChip';
import type { InspectionListRow } from '@/hooks/useChecklistInstancesList';
import { cn } from '@/lib/utils';

interface InspectionKanbanCardProps {
  inspection: InspectionListRow;
  onOpen: (id: string) => void;
  onOpenCustomer?: (inspection: InspectionListRow) => void;
  canOpenCustomer?: boolean;
  /** Abre o modal 360º do veículo vinculado (congelado na criação da vistoria). */
  onOpenVehicle?: (inspection: InspectionListRow) => void;
  canOpenVehicle?: boolean;
}

const DEADLINE_LABELS: Record<string, string> = {
  on_time: 'No prazo',
  start_overdue: 'Início atrasado',
  finish_overdue: 'Conclusão atrasada',
  closed: 'Encerrada',
};

const formatDate = (value: string | null) => value
  ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  : null;

export function InspectionKanbanCard({
  inspection,
  onOpen,
  onOpenCustomer,
  canOpenCustomer = false,
  onOpenVehicle,
  canOpenVehicle = false,
}: InspectionKanbanCardProps) {
  const progress = inspection.items_total
    ? Math.round((inspection.items_answered / inspection.items_total) * 100)
    : 0;
  const overdue = inspection.deadline_status.endsWith('overdue');
  const progressIndicatorClass = progress >= 100
    ? 'bg-emerald-300'
    : progress >= 34
      ? 'bg-amber-300'
      : 'bg-slate-300';
  const progressTextClass = progress >= 100
    ? 'text-emerald-500 dark:text-emerald-300'
    : progress >= 34
      ? 'text-amber-500 dark:text-amber-300'
      : 'text-slate-400';

  return (
    <Card
      role="button"
      tabIndex={0}
      className={cn(
        'p-2.5 cursor-pointer transition-colors hover:border-primary/40 hover:bg-accent/30',
        overdue && 'border-destructive/40',
      )}
      onClick={() => onOpen(inspection.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(inspection.id);
        }
      }}
    >
      {/* Linha compacta: identificação, placa, progresso e alerta antifraude. */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-mono text-[11px] text-muted-foreground">#{inspection.code}</span>
          {/* v1.3.0: placa com affordance de modal do veículo, no mesmo padrão do proprietário.
              v1.7.0: volta ao formato Mercosul compacto (tamanho anterior à padronização
              com as listagens de veículos), preservando a interação de abrir o veículo. */}
          {inspection.vehicle_plate && (
            <div
              role={canOpenVehicle ? 'button' : undefined}
              tabIndex={canOpenVehicle ? 0 : undefined}
              className={cn(
                'group/vehicle flex shrink-0 items-center gap-0.5 rounded-sm transition-colors',
                canOpenVehicle && 'cursor-pointer hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
              title={canOpenVehicle ? 'Abrir veículo em uma janela separada' : inspection.vehicle_plate}
              onClick={(event) => {
                if (!canOpenVehicle) return;
                event.stopPropagation();
                onOpenVehicle?.(inspection);
              }}
              onKeyDown={(event) => {
                if (!canOpenVehicle || (event.key !== 'Enter' && event.key !== ' ')) return;
                event.preventDefault();
                event.stopPropagation();
                onOpenVehicle?.(inspection);
              }}
            >
              <VehiclePlateChip plate={inspection.vehicle_plate} compact />
              {canOpenVehicle && (
                <Car className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/vehicle:opacity-100 group-focus-visible/vehicle:opacity-100" />
              )}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex w-20 items-center gap-1.5" title={`Progresso ${inspection.items_answered}/${inspection.items_total}`}>
            <span className={cn('text-[10px] tabular-nums font-medium', progressTextClass)}>
              {inspection.items_answered}/{inspection.items_total}
            </span>
            <Progress value={progress} className="h-1 flex-1 bg-muted/60" indicatorClassName={cn(progressIndicatorClass, 'transition-colors')} />
          </div>
          {inspection.fraud_alerts > 0 && (
            <span
              className="flex items-center gap-0.5 text-[10px] font-medium tabular-nums text-destructive"
              title={`${inspection.fraud_alerts} alerta(s) de possível fraude`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {inspection.fraud_alerts}
            </span>
          )}
        </div>
      </div>

      <div className="mt-1 flex min-w-0 items-start gap-1.5">
        <p className="min-w-0 flex-1 line-clamp-2 break-words text-xs font-medium leading-tight" title={inspection.template_name}>
          {inspection.template_name}
        </p>
        {inspection.checklist_type_name && (
          <span
            className="max-w-[45%] shrink-0 truncate rounded border bg-muted/50 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground"
            title={`Tipo: ${inspection.checklist_type_name}`}
          >
            {inspection.checklist_type_name}
          </span>
        )}
      </div>
      {/* v1.4.0: data de criação, periodicidade (selo) e origem da vistoria. */}
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1" title="Criada em">
          <CalendarPlus className="h-3 w-3" />{formatDate(inspection.created_at)}
        </span>
        {inspection.schedule_frequency && (
          <span className="rounded border px-1 py-px text-[9px] font-medium uppercase">
            {FREQUENCY_LABELS[inspection.schedule_frequency] ?? inspection.schedule_frequency}
          </span>
        )}
        <span className="flex items-center gap-0.5" title="Origem da vistoria">
          {inspection.origin === 'manual'
            ? <><Hand className="h-3 w-3" />Manual</>
            : <><Repeat className="h-3 w-3" />Automática</>}
        </span>
      </div>
      {inspection.vehicle_label && (
        <p className="mt-1 line-clamp-2 break-words text-[11px] leading-snug text-muted-foreground" title={inspection.vehicle_label}>
          {inspection.vehicle_label}
        </p>
      )}

      {inspection.customer_name && (
        <div
          role={canOpenCustomer ? 'button' : undefined}
          tabIndex={canOpenCustomer ? 0 : undefined}
          className={cn(
            'group/customer mt-1.5 flex min-w-0 items-center gap-1.5 rounded-sm py-0.5 transition-colors',
            canOpenCustomer && 'cursor-pointer hover:bg-accent hover:px-1 hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
          title={canOpenCustomer ? `Abrir ${inspection.customer_name} em uma janela separada` : inspection.customer_name}
          onClick={(event) => {
            event.stopPropagation();
            if (canOpenCustomer) onOpenCustomer?.(inspection);
          }}
          onKeyDown={(event) => {
            if (!canOpenCustomer || (event.key !== 'Enter' && event.key !== ' ')) return;
            event.preventDefault();
            event.stopPropagation();
            onOpenCustomer?.(inspection);
          }}
        >
          <SmartAvatar src={inspection.customer_avatar} name={inspection.customer_name} size="xs" />
          <span className="truncate text-[11px]" title={inspection.customer_name}>{inspection.customer_name}</span>
          {canOpenCustomer && (
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/customer:opacity-100 group-focus/customer:opacity-100" />
          )}
        </div>
      )}

      <div className="mt-1.5 flex items-center justify-between gap-2 border-t pt-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          {inspection.executor_name ? (
            <>
              <SmartAvatar src={inspection.executor_avatar} name={inspection.executor_name} size="xs" />
              <span className="truncate text-[10px] text-muted-foreground" title={inspection.executor_name}>{inspection.executor_name}</span>
            </>
          ) : <span className="text-[10px] text-muted-foreground">Sem responsável</span>}
          {inspection.assignee_mode && (
            <span className="shrink-0 rounded border px-1 py-px text-[9px] text-muted-foreground" title="Tipo de responsável configurado">
              {ASSIGNEE_MODE_LABELS[inspection.assignee_mode] ?? inspection.assignee_mode}
            </span>
          )}
        </div>
        <span className={cn('flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground', overdue && 'text-destructive')}>
          <CalendarClock className="h-3 w-3" />
          {formatDate(inspection.due_finish_at) ?? DEADLINE_LABELS[inspection.deadline_status] ?? 'Sem prazo'}
        </span>
      </div>
    </Card>
  );
}